import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { can } from '@huddle/shared';
import type { Channel } from '@huddle/shared';
import Avatar from '../components/Avatar';
import InstallPrompt from '../components/InstallPrompt';
import Spinner from '../components/Spinner';
import { get } from '../lib/api';
import { shortAgo } from '../lib/time';
import { useSession, useZone } from '../state/session';
import { useChannels } from '../state/unread';

export default function Chats() {
  const { channels, status, reload } = useChannels();
  const { me } = useSession();
  const zone = useZone();
  const [showArchived, setShowArchived] = useState(false);
  const [archived, setArchived] = useState<Channel[]>([]);

  // Fetched separately rather than folded into the polled list: archived
  // channels do not change on their own, and this should not cost a bigger
  // payload every minute on a phone.
  useEffect(() => {
    get<Channel[]>('/channels?archived=1')
      .then((all) => setArchived(all.filter((c) => c.archivedAt !== null)))
      .catch(() => setArchived([]));
  }, [channels.length]);

  return (
    <div className="flex h-full flex-col">
      <header className="pt-safe sticky top-0 z-10 border-b border-zinc-100 bg-surface/95 px-4 pb-3 backdrop-blur">
        <div className="flex items-center justify-between pt-3">
          <div>
            <h1 className="text-xl font-bold text-zinc-900">{me.workspace.name}</h1>
            <p className="text-xs text-zinc-400">The Huddle</p>
          </div>
          <div className="flex items-center gap-1">
            <Link
              to="/search"
              aria-label="Search messages"
              className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 active:bg-zinc-100"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="h-5 w-5"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.5-3.5" strokeLinecap="round" />
              </svg>
            </Link>
            {/* Available to everyone, unlike the new-channel button: messaging
                a colleague is not a management action, and until now the only
                route to a DM was through another channel's settings. */}
            <Link
              to="/new/dm"
              aria-label="New message"
              className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 active:bg-zinc-100"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.2A8.4 8.4 0 0 1 4 11.5a8.5 8.5 0 0 1 8.5-8.5 8.4 8.4 0 0 1 8.5 8.5z" />
              </svg>
            </Link>
            {can(me.role, 'manageChannels') && (
              <Link
                to="/new/channel"
                aria-label="New channel"
                className="flex h-10 w-10 items-center justify-center rounded-full text-zinc-500 active:bg-zinc-100"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  className="h-5 w-5"
                >
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </Link>
            )}
          </div>
        </div>
      </header>

      <div className="scroll-area flex-1">
        <InstallPrompt />

        {status === 'loading' && channels.length === 0 ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : status === 'error' && channels.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <p className="text-sm text-zinc-500">Couldn't load your chats.</p>
            <p className="mt-1 text-xs text-zinc-400">Check your connection.</p>
            <button
              type="button"
              onClick={reload}
              className="mt-4 rounded-xl bg-brand-600 px-5 py-3 font-semibold text-white active:bg-brand-700"
            >
              Try again
            </button>
          </div>
        ) : channels.length === 0 ? (
          <p className="px-6 py-16 text-center text-sm text-zinc-400">
            {can(me.role, 'manageChannels')
              ? 'No channels yet. Tap + to make the first one.'
              : // An associate cannot create one, so telling them the list is
                // empty without saying what happens next reads like a fault.
                "No channels yet. You'll see them here once a manager adds you."}
          </p>
        ) : (
          <ul>
            {channels.map((channel) => (
              <ChannelRow key={channel.id} channel={channel} zone={zone} />
            ))}
          </ul>
        )}

        {/* Archived channels are hidden from the main list by design, but they
            still exist — and until now there was no way back to one, which
            made "archive" a one-way trip and left no route to deleting it. */}
        {archived.length > 0 && (
          <section className="mt-4 border-t border-zinc-100 pt-2">
            <button
              type="button"
              onClick={() => setShowArchived((v) => !v)}
              className="flex w-full items-center justify-between px-4 py-2.5 text-left"
            >
              <span className="text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">
                Archived ({archived.length})
              </span>
              <span
                className={`text-zinc-300 transition-transform ${showArchived ? 'rotate-90' : ''}`}
              >
                ›
              </span>
            </button>
            {showArchived && (
              <ul className="opacity-60">
                {archived.map((channel) => (
                  <ChannelRow key={channel.id} channel={channel} zone={zone} />
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  );
}

function ChannelRow({ channel, zone }: { channel: Channel; zone: string }) {
  const unread = channel.unreadCount > 0;

  return (
    <li>
      <Link to={`/c/${channel.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-zinc-50">
        {channel.type === 'dm' && channel.peer ? (
          <Avatar
            userId={channel.peer.id}
            name={channel.peer.name}
            avatarKey={channel.peer.avatarKey}
          />
        ) : (
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${
              channel.type === 'announcement'
                ? 'bg-amber-100 text-amber-700'
                : 'bg-zinc-100 text-zinc-500'
            }`}
            aria-hidden="true"
          >
            {channel.type === 'announcement' ? (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-4 w-4"
              >
                <path d="M3 11v2a1 1 0 0 0 1 1h2l4 4V6L6 10H4a1 1 0 0 0-1 1z" />
                <path d="M16 8a5 5 0 0 1 0 8" />
              </svg>
            ) : (
              <span className="text-base font-semibold">#</span>
            )}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <span
              className={`truncate ${unread ? 'font-semibold text-zinc-900' : 'font-medium text-zinc-700'}`}
            >
              {channel.name}
            </span>
            {/* `notify` has always been in this payload and was never read, so
                a muted channel looked exactly like an unmuted one — and the
                setting is three taps deep, making "am I muted here?"
                genuinely hard to answer. */}
            {channel.notify === 'muted' && (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-3.5 w-3.5 shrink-0 text-zinc-400"
                role="img"
                aria-label="Muted"
              >
                <path d="M3 3l18 18" />
                <path d="M18.6 13A9 9 0 0 0 19 11v-1a7 7 0 0 0-9.9-6.4" />
                <path d="M5 10v1a7 7 0 0 0 1.6 4.4L5 19h11" />
                <path d="M10 22a2 2 0 0 0 4 0" />
              </svg>
            )}
            {channel.lastMessageAt && (
              <span className="shrink-0 text-[11px] text-zinc-400">
                {shortAgo(channel.lastMessageAt, zone)}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className={`truncate text-sm ${unread ? 'text-zinc-600' : 'text-zinc-400'}`}>
              {channel.lastMessagePreview ?? 'No messages yet'}
            </span>
            {/* A mention badge outranks a plain unread count — being named is
                a different thing from the channel simply being busy. */}
            {channel.mentionCount > 0 ? (
              <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold text-white">
                @{channel.mentionCount}
              </span>
            ) : (
              unread && (
                <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-brand-600 px-1.5 text-[11px] font-bold text-white">
                  {channel.unreadCount > 99 ? '99+' : channel.unreadCount}
                </span>
              )
            )}
          </div>
        </div>
      </Link>
    </li>
  );
}
